/**
 * terminalSerialize — buffer snapshot/restore via localStorage (E.21.24).
 *
 * Layered on top of the always-mounted DualShellLayout: tab toggles already
 * preserve in-memory state. This module covers the orthogonal case of FULL
 * PAGE RELOADS (browser refresh, SPA navigation away and back, restart).
 *
 * On unmount / page unload: serialize each terminal's buffer to localStorage
 * keyed by the WebSocket URL. On remount: read the snapshot and write it
 * back via term.write() before the new WS connects, restoring scrollback
 * and cursor visual state.
 *
 * NOTE: This is a VISUAL-state restore only. The PTY is fresh after a page
 * reload (the WebSocket close terminates the upstream ttyd PTY). Subsequent
 * input goes to a new shell. Acceptable per user directive 2026-05-02.
 */

const STORAGE_KEY_PREFIX = 'vibe-designer:term-buffer:';

// Serialized buffers can be large (xterm.js typically caps at the configured
// scrollback length, default 1000 lines × ~80 chars ≈ 80KB). Skip storing
// anything beyond MAX_SNAPSHOT_BYTES to avoid blowing past localStorage's
// ~5MB-per-origin quota when the user has 5 panes.
const MAX_SNAPSHOT_BYTES = 200_000; // ~200KB per pane; 5 panes = 1MB worst case

export function getStorageKey(wsUrl: string): string {
  return `${STORAGE_KEY_PREFIX}${wsUrl}`;
}

/**
 * Read a previously-saved snapshot for `wsUrl`. Returns null if none, or
 * if the stored value has been corrupted / cleared.
 */
export function loadSerializedBuffer(wsUrl: string): string | null {
  try {
    const value = localStorage.getItem(getStorageKey(wsUrl));
    if (value === null || value === '') return null;
    return value;
  } catch {
    // localStorage can throw in private-browsing modes — just treat as no snapshot.
    return null;
  }
}

/**
 * Persist `data` for `wsUrl`. Silently drops if larger than MAX_SNAPSHOT_BYTES
 * (prevents quota-exceeded errors when long-running sessions accumulate
 * lots of scrollback).
 */
export function saveSerializedBuffer(wsUrl: string, data: string): void {
  if (data.length === 0) return;
  if (data.length > MAX_SNAPSHOT_BYTES) {
    // Truncate to the LAST MAX_SNAPSHOT_BYTES — most recent output is most useful
    // for restoring context (the older scrollback has presumably already been
    // read by the user).
    data = data.slice(data.length - MAX_SNAPSHOT_BYTES);
  }
  try {
    localStorage.setItem(getStorageKey(wsUrl), data);
  } catch {
    // QuotaExceededError or private-browsing — non-critical, skip.
  }
}

/**
 * Drop any stored snapshot for `wsUrl`. Useful if a user wants a "clear all
 * scrollback" UI (not wired in this workstream, but exposed for future use).
 */
export function clearSerializedBuffer(wsUrl: string): void {
  try {
    localStorage.removeItem(getStorageKey(wsUrl));
  } catch {
    // No-op on private-browsing.
  }
}
